import i18n from "i18next";

const TARGET_JA: Record<string, string> = {
  requirements: "要件",
  "the requirements": "要件",
  "workflow steps": "ワークフローステップ",
  "the workflow": "ワークフロー",
  workflow: "ワークフロー",
  "workflow structure": "ワークフロー構造",
  "workflow plan": "ワークフロー計画",
  "your request": "リクエスト",
  "the inputs": "入力",
  inputs: "入力",
  "connected apps": "接続済みアプリ",
  "your connected apps": "接続済みアプリ",
};

function translateTarget(target: string): string {
  const trimmed = target.trim();
  if (!trimmed) return trimmed;
  const lower = trimmed
    .toLowerCase()
    .replace(/[.…:]+$/g, "")
    .trim();
  return TARGET_JA[lower] ?? trimmed;
}

/** Translate SSE notification / todo labels from the workflow agent (English from backend). */
export function translateChatNotification(text: string): string {
  if (!text?.trim()) return text;
  const lang = i18n.language?.split("-")[0] ?? "en";
  if (lang !== "ja") return text;

  const key = `notifications.${normalizeNotificationKey(text)}`;
  const translated = i18n.t(key, { ns: "chat", defaultValue: "" });
  if (translated) return translated;

  // Pattern: "Completed in 1m 2s" / "Stopped in 12s"
  const duration = text.match(/^\s*(Completed|Stopped)\s+in\s+(.+?)\s*$/i);
  if (duration?.[1] && duration[2]) {
    const statusKey =
      duration[1].toLowerCase() === "completed"
        ? "notifications.completedIn"
        : "notifications.stoppedIn";
    return i18n.t(statusKey, {
      ns: "chat",
      duration: duration[2].trim(),
    });
  }

  // Pattern: "Working on: Step Name"
  const working = text.match(/^\s*(Working on|Configuring)\s*:\s*(.+)\s*$/i);
  if (working?.[2]) {
    return i18n.t("notifications.workingOn", {
      ns: "chat",
      step: translateTarget(working[2]),
    });
  }
  const finished = text.match(/^\s*(Finished|Done|Completed)\s*:\s*(.+)\s*$/i);
  if (finished?.[2]) {
    return i18n.t("notifications.finished", {
      ns: "chat",
      step: translateTarget(finished[2]),
    });
  }

  // Generic gerund verb patterns from the backend planner
  // e.g. "Analyzing requirements", "Generating the workflow", "Creating step 2"
  const verbMap: Record<string, string> = {
    analyzing: "notifications.verbAnalyzing",
    generating: "notifications.verbGenerating",
    creating: "notifications.verbCreating",
    planning: "notifications.verbPlanning",
    designing: "notifications.verbDesigning",
    configuring: "notifications.verbConfiguring",
    fetching: "notifications.verbFetching",
    processing: "notifications.verbProcessing",
    validating: "notifications.verbValidating",
    saving: "notifications.verbSaving",
    building: "notifications.verbBuilding",
    connecting: "notifications.verbConnecting",
    sending: "notifications.verbSending",
    updating: "notifications.verbUpdating",
    refining: "notifications.verbRefining",
    defining: "notifications.verbDefining",
    executing: "notifications.verbExecuting",
    running: "notifications.verbRunning",
    preparing: "notifications.verbPreparing",
    finalizing: "notifications.verbFinalizing",
  };
  const verb = text.match(/^\s*([A-Za-z]+ing)\b\s*(.*)$/);
  if (verb) {
    const verbKey = verb[1]!.toLowerCase();
    const rest = (verb[2] ?? "").replace(/[.…:]+$/, "").trim();
    const mapKey = verbMap[verbKey];
    if (mapKey) {
      return i18n.t(mapKey, {
        ns: "chat",
        target: translateTarget(rest),
      });
    }
  }

  return text;
}

/**
 * Backend notification text → stable snake_case key. Exported because callers
 * outside translation also need to recognise specific notifications (the to-do
 * list filters out the "waiting for input" one), and matching raw strings there
 * would be a third spelling of this same normalisation.
 */
export function normalizeNotificationKey(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function getChatLocale(): string {
  const fromI18n = i18n.language?.split("-")[0];
  if (fromI18n) return fromI18n;
  if (typeof document !== "undefined") {
    return document.documentElement.lang?.split("-")[0] ?? "en";
  }
  return "en";
}

/** Standard locale field for workflow chat multipart requests. */
export function appendChatLocaleToFormData(formData: FormData): void {
  formData.set("locale", getChatLocale());
}
